import React from 'react';
import Icon from '../../../components/AppIcon';

const ProjectStats = ({ stats }) => {
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(1)) + ' ' + sizes?.[i];
  };

  const getUsagePercentage = (used, total) => {
    return Math.min((used / total) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-primary';
  };

  const statCards = [
    {
      title: 'Total Projects',
      value: stats?.totalProjects,
      limit: stats?.projectLimit,
      icon: 'FolderOpen',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Storage Used',
      value: formatBytes(stats?.storageUsed),
      limit: formatBytes(stats?.storageLimit),
      icon: 'HardDrive',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      percentage: getUsagePercentage(stats?.storageUsed, stats?.storageLimit)
    },
    {
      title: 'Renders This Month',
      value: stats?.rendersThisMonth,
      limit: stats?.renderLimit,
      icon: 'Video',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Active Renders',
      value: stats?.activeRenders,
      limit: stats?.maxConcurrentRenders,
      icon: 'Play',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
      {statCards?.map((stat, index) => {
        const usagePercentage = stat?.percentage || getUsagePercentage(stat?.value, stat?.limit);
        const isOverLimit = usagePercentage >= 100;
        
        return (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 ${stat?.bgColor} rounded-lg flex items-center justify-center`}>
                <Icon name={stat?.icon} size={20} className={stat?.color} />
              </div>
              {isOverLimit && (
                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                  <Icon name="AlertTriangle" size={12} className="text-red-600" />
                </div>
              )}
            </div>
            <div className="mb-2">
              <h3 className="text-sm font-medium text-muted-foreground">{stat?.title}</h3>
              <div className="flex items-baseline space-x-2">
                <span className="text-2xl font-bold text-foreground">
                  {typeof stat?.value === 'number' ? stat?.value?.toLocaleString() : stat?.value}
                </span>
                {stat?.limit && (
                  <span className="text-sm text-muted-foreground">
                    / {typeof stat?.limit === 'number' ? stat?.limit?.toLocaleString() : stat?.limit}
                  </span>
                )}
              </div>
            </div>
            {/* Progress Bar */}
            {stat?.limit && (
              <div className="space-y-1">
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(usagePercentage)}`}
                    style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{usagePercentage?.toFixed(1)}% used</span>
                  {isOverLimit && (
                    <span className="text-red-600 font-medium">Over limit</span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProjectStats;